import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { ArrowBack } from '@mui/icons-material';
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import { useParams, useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { useCompleteEmailConnectionMutation } from 'src/services/email-sync/email-sync.service';

import Logo from 'src/components/logo';
import { getCompleteConnectionErrorMessage } from 'src/services/email-sync/email-sync.utils';

// TODO add cancelation detection
// Callback http://localhost:3000/auth/callbacks/email-connector?error=oauth_failed&error_code=45002&error_description=Error+occurred+during+authentication%3A+consent_required

export default function EmailConnectorCallbackManagerView() {

  const { t } = useTranslate();

  const router = useRouter();
  const { provider } = useParams();
  const searchParams = useSearchParams();

  const [view, setView] = useState('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [completeConnection] = useCompleteEmailConnectionMutation();

  const handleConnect = useCallback(async (code: string, state: string = '') => {
    setView('connecting');

    try {

      await completeConnection({
        code
      }).unwrap();

      setView('success');

      setTimeout(() => {
        router.push(paths.dashboard.account.email);
      }, 3000);
    }
    catch (error) {
      setView('error');
      setErrorMessage(getCompleteConnectionErrorMessage(error));
    }
  }, [router, completeConnection]);

  useEffect(() => {
    const code = searchParams.get('code');

    if (!code) {
      setView('error');
      return;
    }

    handleConnect(code);
  }, [provider, searchParams, handleConnect]);

  const handleGoBack = () => {
    router.push(paths.dashboard.account.root);
  };

  const renderContent = () => {
    switch (view) {
      case 'loading':
      case 'connecting':
        return (
          <>
            <Typography variant="h6">
              {t('email-connector.connect.linking', { provider: _.capitalize(provider) })}
            </Typography>
            <CircularProgress size={30} />
          </>
        );
      case 'success':
        return (
          <>
            <Alert severity="success">
              {t('email-connector.connect.success', { provider: _.capitalize(provider) })}
            </Alert>
            <CircularProgress size={30} />
            <Typography variant='body2'>{t('email-connector.connect.redirecting')}</Typography>
          </>
        );
      case 'error':
        return (
          <>
            <Alert severity="warning" sx={{ textAlign: 'center' }} icon={false}>
              {errorMessage || t('email-connector.connect.error', { provider: _.capitalize(provider) })}
            </Alert>
            <Button startIcon={<ArrowBack />} variant="contained" color="primary" onClick={handleGoBack}>
              {t('Go Back')}
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Stack
      spacing={4}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ backgroundColor: 'background.default' }}
    >
      <Paper sx={{ p: 4, borderRadius: 2, maxWidth: 400 }}>
        <Stack spacing={3} alignItems="center">
          <Logo />
          {renderContent()}
        </Stack>
      </Paper>
    </Stack>
  );
}